import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxPermissionsModule } from 'ngx-permissions';
import { RouterModule } from '@angular/router';
import {
  NgbAccordionModule,
  NgbAlertModule,
  NgbCollapseModule,
  NgbDatepickerModule,
  NgbModule,
  NgbNavModule,
  NgbPopoverModule,
  NgbProgressbarModule,
  NgbTimepickerModule,
  NgbTooltipModule
} from '@ng-bootstrap/ng-bootstrap';
import {
  ActionBarComponent,
  ApplicationNameCellRendererComponent,
  AttachmentsWorkspaceBreadcrumbComponent,
  AttachmentsWorkspaceTableComponent,
  BreadcrumbComponent,
  ButtonWithLoaderComponent,
  DashboardCardComponent,
  DetailsTableComponent,
  DocumentLockComponent,
  DocumentWorkInProgressComponent,
  FormActionsComponent,
  FormSectionContainerComponent,
  HeaderComponent,
  IconComponent,
  ImageRendererComponent,
  InputCodeFormComponent,
  InputTextConditionalCheckboxComponent,
  LabelItemComponent,
  LoaderComponent,
  NavbarComponent,
  NoActiveModuleComponent,
  ResourceNavigationComponent,
  ScrollableNavTabComponent,
  SidebarComponent,
  StorageComponent,
  TablePageLayoutComponent,
  WarningComponent
} from './components';
import { DashboardLayoutComponent } from './layout/dashboard-layout.component';
import {
  SidebarItemButtonComponent,
  SidebarItemComponent,
  SidebarItemFooterComponent,
  SidebarItemLinkComponent,
  SidebarItemLogoComponent,
  SidebarItemMenuComponent,
  SidebarItemStorageComponent,
  SidebarItemTitleComponent,
  SidebarItemToogleComponent
} from './components/sidebar-items';
import {
  CandidatesMenuItemComponent,
  NavbarAnyPermissionComponent,
  NavbarButtonItemComponent,
  NavbarLegalEntityItemComponent,
  NavbarLogoItemComponent,
  NavbarMenuItemComponent,
  NavbarSessionNotificationItemComponent,
  NavbarSupportItemComponent,
  NavbarUserItemComponent,
  NavItemComponent
} from './components/nav-item';
import {
  BooleanCellRendererComponent,
  BooleanToAdverbFilterComponent,
  BooleanToAdverbsRendererComponent,
  CheckboxRendererComponent,
  CustomDateFilterComponent,
  CustomRendererComponent,
  CustomTooltipComponent,
  CveDetailsRendererComponent,
  DateFormatterRendererComponent,
  EnumRendererComponent,
  EnumToAdverbFilterComponent,
  EnumToAdverbsFilterCellRendererComponent,
  FileSizeFormatterRendererComponent,
  FooterRenderComponent,
  LevelFormatterRendererComponent,
  ListFormatterRendererComponent,
  ListIndexFormatterRendererComponent,
  LockFormatterRendererComponent,
  OverlayLoadingRendererComponent,
  ProgressFormatterRendererComponent,
  RegulationTypeRendererComponent,
  ResourceSubTypeFormatterRendererComponent,
  ScenarioFormatterRendererComponent,
  SelectCellEditableRendererComponent,
  SeverityFormatterRendererComponent,
  StatusFormatterRendererComponent,
  TableColActionsComponent,
  TableFilterComponent,
  TextCellEditableRendererComponent,
  TranslateServiceRendererComponent
} from './ag-grid';
import {
  AttachFileDialogComponent,
  CreateDataBreachNoteDialogComponent,
  CreateFolderDialogComponent,
  CreateSnapshotDialogComponent,
  CreateSupplierDialogComponent,
  DbHistoryDialogComponent,
  EditEntityPriorityDialogComponent,
  ErrorDependencyDialogComponent,
  ImportFileWithCheckComponent,
  IncidentTeamDialogComponent,
  MoveItemDialogComponent,
  NewGroupDialogComponent,
  RenameResourceDialogComponent,
  SelectRecipientDialogComponent,
  SelectRecipientTableComponent,
  SelectUserResourceDialogComponent,
  SelectUserResourceTableComponent,
  ShowAllAttributesDialogComponent,
  UpdateDataBreachDialogComponent,
  ValidateRequestDialogComponent
} from './dialogs';
import {
  AttachmentTypePipe,
  FormatNewlineToHtmlPipe, FormatTextPipe,
  HighlightPipe,
  LatestChangeDatePipe,
  LocalizedDatePipe,
  ReferenceTitlePipe,
  ReplaceNullWithTextPipe,
  SafeUrlPipe
} from './pipes';
import { SessionNotificationComponent } from './components/session-info/session-notification.component';
import { SessionNotificationItemComponent } from './components/session-info/session-notification-item.component';
import { LevelFormatterComponent } from './components/level-formatter.component';
import { AgGridModule } from 'ag-grid-angular';
import { TranslateModule } from '@ngx-translate/core';
import { IonicModule } from '@ionic/angular';
import {
  DetailsAdministratorsComponent,
  DetailsAttachmentsComponent,
  DetailsAttributeComponent,
  DetailsAttributeViewerComponent,
  DetailsCardComponent,
  DetailsChartComponent,
  DetailsImageComponent,
  DetailsLayoutComponent,
  DetailsPageHeaderComponent,
  DetailsPageWrapperComponent,
  DetailsReminderComponent,
  DetailsSecurityToolTypeComponent,
  DetailsTitleComponent,
  DetailsViewerComponent,
  RequiredReloadComponent
} from './components/details';
import { NgxEchartsModule } from 'ngx-echarts';
import { EntityIconComponent } from './components/entity-icon.component';
import {
  DeleteSelectedRowsComponent
} from './components/action-bar/components/delete-selected-rows/delete-selected-rows.component';
import { InputLabelComponent } from './components/input-label/input-label.component';
import { InputTimeDatePickerComponent } from './components/input-time-date-picker/input-time-date-picker.component';
import { QuillModule } from 'ngx-quill';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { UpdateFormFooterActionsComponent } from './components/update-form-footer-actions.component';
import {
  DownloadSelectedRowsComponent
} from './components/action-bar/components/download-selected-rows/download-selected-rows.component';
import { DeleteWithConfirmComponent } from './components/delete-with-confirm-inline/delete-with-confirm.component';
import { EditHelpComponent } from './components/edit-help.component';
import { RemoveHelpPrefixPipe } from './pipes/remove-help-prefix.pipe';
import {
  DpiaEvaluationMapComponent,
  DpiaStatsEditorComponent,
  DpiaStatsTableComponent,
  NoAvailableStatsComponent,
  RiskAssessmentStatsComponent
} from './documents/stats';
import { NoAvailableCommentComponent } from './documents/comments/no-available-comment.component';
import { DpiaCommentsComponent } from './documents/comments/dpia-comments.component';
import {
  EditorChapterStatusSelectorComponent,
  EditorSidebarComponent, EditorSidebarDownloadWordBtnComponent,
  EditorSidebarLinkComponent,
  EditorSidebarNavigationDataComponent,
  EditorSidebarTextComponent,
  GlobalMeasureCheckEditorComponent,
  InputAttachmentComponent,
  InputCheckboxComponent,
  InputConfirmPasswordComponent,
  InputDocumentPermissionSelectorComponent,
  InputEmailComponent,
  InputErrorMessageComponent,
  InputFilteredMultipleSelectComponent,
  InputFilteredSelectComponent,
  InputFilteredSelectWithFooterActionComponent,
  InputImageComponent,
  InputNumberComponent,
  InputPasswordComponent,
  InputPasswordHelpComponent,
  InputPhoneComponent,
  InputRadioComponent,
  InputSelectComponent,
  InputSelectVersionComponent,
  InputTextAreaComponent,
  InputTextComponent,
  InputUserChooserComponent,
  InputUserGroupSelectComponent,
  InputUsernameComponent,
  InputUserPermissionSelectComponent,
  RemoveBoxComponent,
  TemplateCompleteStatusComponent,
  TemplateEditorGroupsComponent,
  TemplateEditorViewSelectorComponent,
  TemplateEditorWidgetGroupComponent,
  TemplateHeaderComponent,
  TreatmentEditorComponent,
  TreatmentInputComponent,
  TreatmentInputNavigationDataComponent,
  TreatmentInputRiskComponent,
  TreatmentInputStateComponent,
  WidgetSelectorDirective
} from './documents';
import {
  TreatmentActionStateComponent
} from './documents/widget-component/treatment-input-state/components/treatment-action-state.component';
import { DocumentEditorNotificationComponent } from './documents/dialogs/document-editor-notification.component';
import { WidgetNotificationComponent } from './documents/dialogs/widget-notification.component';
import { WidgetCheckboxComponent } from './documents/widgets/widget-checkbox/widget-checkbox.component';
import { WidgetCheckboxV0Component } from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0.component';
import {
  WidgetCheckboxV0PreviewComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0.preview.component';
import {
  WidgetCheckboxV0EditComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0.edit.component';
import {
  WidgetCheckboxV0EditItemComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0.edit-item.component';
import {
  WidgetContactChooserComponent
} from './documents/widgets/widget-contact-chooser/widget-contact-chooser.component';
import {
  WidgetContactChooserV0Component
} from './documents/widgets/widget-contact-chooser/v0/widget-contact-chooser-v0.component';
import {
  WidgetContactChooserV0RowComponent
} from './documents/widgets/widget-contact-chooser/v0/widget-contact-chooser-v0-row.component';
import {
  WidgetContactChooserV0PreviewComponent
} from './documents/widgets/widget-contact-chooser/v0/widget-contact-chooser-v0-preview.component';
import {
  WidgetContactChooserV0EditComponent
} from './documents/widgets/widget-contact-chooser/v0/widget-contact-chooser-v0-edit.component';
import {
  WidgetRadiobuttonPreviewComponent
} from './documents/widgets/widget-radiobutton/widget-radiobutton-preview.component';
import { WidgetRadiobuttonComponent } from './documents/widgets/widget-radiobutton/widget-radiobutton.component';
import {
  WidgetRadiobuttonV0Component
} from './documents/widgets/widget-radiobutton/v0/widget-radiobutton-v0.component';
import {
  WidgetRadiobuttonV0PreviewComponent
} from './documents/widgets/widget-radiobutton/v0/widget-radiobutton-v0-preview.component';
import { WidgetDpiaComponent } from './documents/widgets/widget-dpia/widget-dpia.component';
import { WidgetDpiaV0Component } from './documents/widgets/widget-dpia/v0/widget-dpia-v0.component';
import { WidgetDpiaPreviewComponent } from './documents/widgets/widget-dpia/widget-dpia-preview.component';
import { WidgetDpiaV0EditComponent } from './documents/widgets/widget-dpia/v0/widget-dpia-v0-edit.component';
//todo check other import files @georgy
import {
  WidgetDpiaV0GravityValueSelectorComponent
} from './documents/widgets/widget-dpia/v0/widget-dpia-v0-gravity-value-selector.component';
import {
  WidgetSimpleRadioButtonComponent
} from './documents/widgets/widget-simple-radiobutton/widget-simple-radio-button.component';
import {
  WidgetSimpleRadiobuttonV0Component
} from './documents/widgets/widget-simple-radiobutton/v0/widget-simple-radiobutton-v0.component';
import {
  WidgetSimpleRadiobuttonV0PreviewComponent
} from './documents/widgets/widget-simple-radiobutton/v0/widget-simple-radiobutton-v0-preview.component';
import { WidgetTextV0PreviewComponent } from './documents/widgets/widget-text/v0/widget-text-v0-preview.component';
import { WidgetTextComponent } from './documents/widgets/widget-text/widget-text.component';
import { WidgetTextV0Component } from './documents/widgets/widget-text/v0/widget-text-v0.component';
import { WidgetDateComponent } from './documents/widgets/widget-date/widget-date.component';
import { WidgetDateV0Component } from './documents/widgets/widget-date/v0/widget-date-v0.component';
import { WidgetDateV0EditComponent } from './documents/widgets/widget-date/v0/widget-date-v0-edit.component';
import { WidgetDateV0PreviewComponent } from './documents/widgets/widget-date/v0/widget-date-v0-preview.component';
import {
  WidgetAppAssessmentComponent
} from './documents/widgets/widget-app-assessment/widget-app-assessment.component';
import {
  WidgetAppAssessmentV0Component
} from './documents/widgets/widget-app-assessment/v0/widget-app-assessment-v0.component';
import {
  WidgetAppAssessmentTableHeaderV0Component
} from './documents/widgets/widget-app-assessment/v0/widget-app-assessment-table-header-v0.component';
import { WidgetAppSummaryComponent } from './documents/widgets/widget-app-summary/widget-app-summary.component';
import { WidgetAppSummaryV0Component } from './documents/widgets/widget-app-summary/v0/widget-app-summary-v0.component';
import {
  WidgetAppSummaryPreviewComponent
} from './documents/widgets/widget-app-summary/widget-app-summary-preview.component';
import {
  WidgetAppSummaryPreviewV0Component
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-preview-v0.component';
import {
  WidgetRiskAssessmentComponent
} from './documents/widgets/widget-risk-assessment/widget-risk-assessment.component';
import {
  WidgetPreliminaryRiskAssessmentComponent
} from './documents/widgets/widget-preliminary-risk-assessment/widget-preliminary-risk-assessment.component';
import {
  WidgetPreliminaryRiskAssessmentV0Component
} from './documents/widgets/widget-preliminary-risk-assessment/v0/widget-preliminary-risk-assessment-v0.component';
import {
  WidgetPreliminaryRiskAssessmentV0TableComponent
} from './documents/widgets/widget-preliminary-risk-assessment/v0/widget-preliminary-risk-assessment-v0-table/widget-preliminary-risk-assessment-v0-table.component';
import {
  WidgetRecipientChooserComponent
} from './documents/widgets/widget-recipient-chooser/widget-recipient-chooser.component';
import {
  WidgetRecipientChooserV0Component
} from './documents/widgets/widget-recipient-chooser/v0/widget-recipient-chooser-v0/widget-recipient-chooser-v0.component';
import {
  WidgetRecipientChooserV0PreviewComponent
} from './documents/widgets/widget-recipient-chooser/v0/widget-recipient-chooser-v0-preview.component';
import {
  WidgetRecipientChooserV0EditComponent
} from './documents/widgets/widget-recipient-chooser/v0/widget-recipient-chooser-v0-edit.component';
import {
  WidgetDataRetentionAndDeletionComponent
} from './documents/widgets/widget-data-retention-and-deletion/widget-data-retention-and-deletion.component';
import {
  WidgetDataRetentionAndDeletionV0Component
} from './documents/widgets/widget-data-retention-and-deletion/v0/widget-data-retention-and-deletion-v0.component';
import {
  WidgetDataRetentionAndDeletionV0RowComponent
} from './documents/widgets/widget-data-retention-and-deletion/v0/widget-data-retention-and-deletion-v0-row/widget-data-retention-and-deletion-v0-row.component';
import {
  WidgetDataRetentionAndDeletionHeaderV0Component
} from './documents/widgets/widget-data-retention-and-deletion/v0/widget-data-retention-and-deletion-header-v0.component';
import {
  WidgetDataRetentionAndDeletionPreviewV0Component
} from './documents/widgets/widget-data-retention-and-deletion/v0/widget-data-retention-and-deletion-preview-v0.component';
import {
  WidgetTreatmentDataInterestedComponent
} from './documents/widgets/widget-treatment-data-interested/widget-treatment-data-interested.component';
import {
  WidgetTreatmentDataInterestedV0Component
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0.component';
import {
  WidgetTreatmentDataInterestedV0EditComponent
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0-edit.component';
import {
  WidgetTreatmentDataInterestedV0PreviewComponent
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0-preview.component';
import {
  WidgetTreatmentDataInterestedV0TableComponent
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0-table/widget-treatment-data-interested-v0-table.component';
import {
  WidgetTreatmentDataInterestedV0ListCheckboxComponent
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0-list-checkbox/widget-treatment-data-interested-v0-list-checkbox.component';
import {
  WidgetTreatmentDataInterestedV0GroupCheckboxComponent
} from './documents/widgets/widget-treatment-data-interested/v0/widget-treatment-data-interested-v0-group-checkbox/widget-treatment-data-interested-v0-group-checkbox.component';
import {
  WidgetMultiQuestionComponent
} from './documents/widgets/widget-multi-question/widget-multi-question.component';
import {
  WidgetMultiQuestionV0Component
} from './documents/widgets/widget-multi-question/v0/widget-multi-question-v0.component';
import {
  WidgetMultiQuestionV0PreviewComponent
} from './documents/widgets/widget-multi-question/v0/widget-multi-question-v0-preview.component';
import {
  WidgetMultiQuestionV0EditorComponent
} from './documents/widgets/widget-multi-question/v0/widget-multi-question-v0-editor.component';
import { WidgetAttachmentComponent } from './documents/widgets/widget-attachment/widget-attachment.component';
import { WidgetAttachmentV0Component } from './documents/widgets/widget-attachment/v0/widget-attachment-v0.component';
import {
  WidgetAttachmentEditorV0Component
} from './documents/widgets/widget-attachment/v0/widget-attachment-editor-v0.component';
import {
  WidgetAttachmentPreviewV0Component
} from './documents/widgets/widget-attachment/v0/widget-attachment-preview-v0.component';
import { WidgetHtmlPreviewComponent } from './documents/widgets/widget-html/widget-html-preview.component';
import { WidgetHtmlComponent } from './documents/widgets/widget-html/widget-html.component';
import { WidgetHtmlV0Component } from './documents/widgets/widget-html/v0/widget-html-v0.component';
import { WidgetHtmlV0PreviewComponent } from './documents/widgets/widget-html/v0/widget-html-v0-preview.component';
import {
  TabWithScrollComponent
} from './documents/editor/template-editor-groups/tab-with-scroll/tab-with-scroll.component';
import { WidgetInputNoteComponent } from './documents/widget-component/widget-input-note/widget-input-note.component';
import { WidgetDisabledComponent } from './documents/widgets/widget-disabled.component';
import {
  WidgetVersionMissingComponent
} from './documents/widget-component/widget-version-missing-components.component';
import { WidgetHeaderComponent } from './documents/widget-component/widget-header/widget-header.component';
import { WidgetNodataComponent } from './documents/widgets/widget-nodata.component';
import { WidgetActionComponent } from './documents/widget-component/widget-action/widget-action.component';
import { WidgetFooterComponent } from './documents/widget-component/widget-footer.component';
import { WidgetNotSupportedComponent } from './documents/widgets/widget-not-supported.component';
import { FullDialogHeaderComponent } from './documents/dialogs/full-dialog-header.component';
import {
  WidgetRecipientChooserV0RowComponent
} from './documents/widgets/widget-recipient-chooser/v0/widget-recipient-chooser-v0-row/widget-recipient-chooser-v0-row.component';
import { WidgetErrorComponent } from './documents/widgets/widget-error.component';
import { WidgetSelectComponent } from './documents/widget-component/widget-select/widget-select.component';
import { HtmlPreviewDialogComponent } from './documents/dialogs/html-preview-dialog.component';
import { TextareaDialogComponent } from './documents/dialogs/textarea-dialog.component';
import { WidgetConfirmDialogComponent } from './documents/dialogs/widget-confirm-dialog.component';
import { PtWidgetModuleConfig, WidgetConfig } from '@pt/pt-core';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import {
  AuthImageAreaComponent,
  ForgotPasswordComponent,
  ForgotPasswordSendMailComponent,
  ForgotPasswordUpdateComponent,
  ImpersonationDialogComponent,
  SignInComponent,
  SignLayoutComponent
} from './pt-auth';
import { SignHeaderComponent } from './pt-auth/sign-header.component';
import { ForgotMessageComponent } from './pt-auth/forgot-message.component';
import { SignActionsComponent } from './pt-auth/sign-actions.component';
import { PdfActionComponent } from './components/pdf-action/pdf-action.component';
import {
  ConfirmDeleteDocumentValidatedDialogComponent
} from './dialogs/confirm-delete-document-validated-dialog.component';
import { CreateNewDocumentResponseDialogComponent } from './dialogs/create-new-document-response-dialog.component';
import { UserCheckedDialogComponent } from './dialogs/user-checked-dialog.component';
import {
  ConfirmDeleteDataProtectionGovernanceItemDialogComponent
} from './dialogs/confirm-delete-data-protection-governance-item-dialog.component';
import { LoadingPageComponent } from './components/loading-page.component';
import { InfoComponent } from './components/info/info.component';
import { MissingImgDirective } from './components/icon/missing-img.directive';
import {
  WidgetCheckboxV0TreeComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0-tree/widget-checkbox-v0-tree.component';
import {
  WidgetCheckboxV0SelectComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0-select/widget-checkbox-v0-select.component';
import {
  WidgetCheckboxV0TreeRendererComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0-tree/widget-checkbox-v0-tree-renderer.component';
import {
  WidgetCheckboxV0TreeDialogComponent
} from './documents/widgets/widget-checkbox/v0/dialog/widget-checkbox-v0-tree-dialog.component';
import { PermissionDialogComponent } from './dialogs/permission-dialog';
import { PermissionIconButtonComponent } from './dialogs/permission-dialog/permission-icon-button.component';
import { UnexpectedErrorComponent } from './pages';
import { ApiClientModule } from '@pt/api-client';
import {
  DetailsSecurityToolTypeInstructionsComponent
} from './components/details/details-security-tool-type/details-security-tool-type-instructions.component';
import { VendorAndProductDialogComponent } from './dialogs/vendor-and-product-dialog.component';
import {
  DetailsApplicationAuditComponent
} from './components/details/details-application-audit/details-application-audit.component';
import {
  DetailsApplicationAuditCveSeverityComponent
} from './components/details/details-application-audit/details-application-audit-cve-severity.component';
import {
  ImpactMetricValueViewerComponent
} from './components/impact-metric-viewer/impact-metric-value-viewer.component';
import {
  WidgetMultiQuestionV0CheckButtonComponent
} from './documents/widgets/widget-multi-question/v0/selectors/widget-multi-question-v0-check-button.component';
import {
  WidgetMultiQuestionV0TextComponent
} from './documents/widgets/widget-multi-question/v0/selectors/widget-multi-question-v0-text.component';
import {
  WidgetMultiQuestionV0RadioButtonComponent
} from './documents/widgets/widget-multi-question/v0/selectors/widget-multi-question-v0-radio-button.component';
import { WaaEditorComponent } from './documents/widgets/widget-app-assessment/v0/waa-editor.component';
import { TreatmentInfoDialogComponent } from './dialogs/treatment-info-dialog.component';
import { RidViewerComponent } from './components/rid-viewer.component';

import {
  TreatmentEditorPlaceholderComponent
} from './documents/editor/editor-document/treatment-editor-placeholder.component';
import {
  EditorSidebarPanelGroupComponent
} from './documents/editor/editor-sidebar-panel-group/editor-sidebar-panel-group.component';
import {
  EditorSidebarPreviewBtnComponent
} from './documents/editor/editor-sidebar/editor-sidebar-preview-btn.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { EditorLayoutComponent } from './documents/editor/editor-layout/editor-layout.component';
import { EditorNotificationComponent } from './documents/editor/editor-notification.component';
import { AiResultComponent } from './documents/editor/ai-result.component';
import { TreatmentResultComponent } from './documents/editor/treatment-result.component';
import {
  TemplateEditorGroupComponent
} from './documents/editor/template-editor-groups/template-editor-group.component';
import { EditorSidebarMenuBtnComponent } from './documents/editor/editor-sidebar/editor-sidebar-menu-btn.component';
import { AiAssEditorComponent } from './documents/editor/editor-document/ai-ass-editor.component';
import { AppAssEditorComponent } from './documents/editor/editor-document/app-ass-editor.component';
import { CovidAssEditorComponent } from './documents/editor/editor-document/covid-ass-editor.component';
import { AppResultComponent } from './documents/editor/app-result.component';
import { CovidResultComponent } from './documents/editor/covid-result.component';
import { SidebarModule } from 'ng-sidebar-v2';
import { DetailsListCardComponent } from './components/details/details-list-card/details-list-card.component';
import { TreatmentDetailsResultComponent } from './documents/editor/treatment-details-result.component';
import { EditDocumentValidatedDialogComponent } from './dialogs/edit-document-validated-dialog.component';
import { DetailsSingleValueComponent } from './components/details/details-single-value.component';
import { FormContainerComponent } from './components/form-section-container/form-container.component';
import {
  WidgetAppSummaryRowV0Component
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-row-v0.component';
import {
  WidgetAppSummaryRowAppV0Component
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-row-app-v0.component';
import { InputDatePickerComponent } from './components/input-time-date-picker/input-date-picker.component';
import {
  WidgetCheckboxV0EditChildrenComponent
} from './documents/widgets/widget-checkbox/v0/widget-checkbox-v0.edit-children.component';
import { EditorListCardComponent } from './components/editor/editor-list-card/editor-list-card.component';
import {
  WidgetAppSummaryV0WarningComponent
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-v0-warning.component';
import { WidgetLayoutComponent } from './documents/layout/widget-layout.component';
import { WraV0Component } from './documents/widgets/widget-risk-assessment/v1/wra-v0.component';
import { WraV0EditorComponent } from './documents/widgets/widget-risk-assessment/v1/editor/wra-v0-editor.component';
import {
  WraV0ImplementedValueSelectorComponent
} from './documents/widgets/widget-risk-assessment/v1/editor/wra-v0-implemented-value-selector.component';
import {
  WraV0GlobalMeasureSelectorComponent
} from './documents/widgets/widget-risk-assessment/v1/editor/wra-v0-global-measure-selector.component';
import { WaaEditRowCol2Component } from './documents/widgets/widget-app-assessment/v0/waa-edit-row-col2.component';
import { WaaEditRowCol1Component } from './documents/widgets/widget-app-assessment/v0/waa-edit-row-col1.component';
import { WaaEditRowCol3Component } from './documents/widgets/widget-app-assessment/v0/waa-edit-row-col3.component';
import { WaaEditRowCol4Component } from './documents/widgets/widget-app-assessment/v0/waa-edit-row-col4.component';
import { TemplateHeaderActionsComponent } from './documents/editor/document-header/template-header-actions.component';
import {
  DocumentActionDialogComponent
} from './documents/dialogs/publish-reopen-document-dialog/document-action-dialog.component';
import { DocumentSettingsDialogComponent } from './dialogs/document-settings-dialog.component';
import { WrcV0PreviewComponent } from './documents/widgets/widget-recipient-chooser/v1/wrc-v0-preview.component';
import { WrcV0EditorComponent } from './documents/widgets/widget-recipient-chooser/v1/wrc-v0-editor.component';
import {
  WidgetRadiobuttonV0RadioComponent
} from './documents/widgets/widget-radiobutton/v0/widget-radiobutton-v0-radio.component';
import { WrcV0RowComponent } from './documents/widgets/widget-recipient-chooser/v1/wrc-v0-row/wrc-v0-row.component';
import { TabChapterComponent } from './documents/editor/template-editor-groups/tab-chapter.component';
import { NoActiveManagementModuleComponent } from './components/no-active-entity/no-active-management-module.component';
import {
  WidgetRadiobuttonV0SelectComponent
} from './documents/widgets/widget-radiobutton/v0/widget-radiobutton-v0-select.component';
import { HtmlHelpDialogComponent } from './dialogs/html-help-dialog.component';
import { ConfirmObsoleteDialogComponent } from './dialogs/confirm-obsolete-dialog.component';
import { CveDetailsTableComponent } from './ag-grid/renderers/cve-details-table.component';
import { SystemLogDialogComponent } from './dialogs/system-log-dialog.component';
import {
  DetailsApplicationCveComponent
} from './components/details/details-application-cve/details-application-cve.component';
import {
  DetailsSecurityToolTypeSupplierComponent
} from './components/details/details-security-tool-type/details-security-tool-type-supplier.component';
import {
  DetailsSecurityToolTypeVendorOrProductComponent
} from './components/details/details-security-tool-type/details-security-tool-type-vendor-or-product.component';
import { WidgetTextV0AutosaveComponent } from './documents/widgets/widget-text/v0/widget-text-v0-autosave.component';
import { WidgetDateV0AutosaveComponent } from './documents/widgets/widget-date/v0/widget-date-v0-autosave.component';
import { InputMultipleTextComponent } from './documents/components/input-multiple-text/input-multiple-text.component';
import { NavbarUserItemLayoutComponent } from './components/nav-item/custom-item/navbar-user-item-layout.component';
import { EditorSummaryComponent } from './documents/editor/editor-summary/editor-summary.component';
import { CtrlClickDirective, IfDeleteActionDirective } from '@pt/directives';
import { DevWidgetInfoComponent } from '@pt/dev';
import { TreeModule } from '@pt/tree';
import { WidgetHelpComponent } from '@pt/help';
import { DocumentHistoryDialogComponent } from './documents/dialogs/document-history-dialog.component';
import { InputTimestampComponent } from '@pt/form';
import { WMeasureCategoryTypeBadgeComponent } from '@pt/widgets';
import { TextWithDialogComponent } from './components/text-with-dialog.component';
import { CommentDialogComponent } from './documents/dialogs/comment-dialog.component';
import { ExtractionCardComponent } from './components/page/extraction-card.component';
import {
  WidgetAppSummaryRowRidV0Component
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-row-rid-v0.component';
import {
  WidgetAppSummaryRowRidV2V0Component
} from './documents/widgets/widget-app-summary/v0/widget-app-summary-row-rid-v2-v0.component';
import { CompleteStatusDialogComponent } from './documents/dialogs/complete-status-dialog.component';
import { OperationTypeBadgeViewerComponent } from '@primatoo-ui/components';
import { OtpInputComponent } from './pt-auth/sign-in/otp-input.component';

const DOCUMENT_STATS_AND_COMMENT = [
  NoAvailableStatsComponent,
  NoAvailableCommentComponent,
  DpiaStatsEditorComponent,
  DpiaStatsTableComponent,
  DpiaCommentsComponent,
  RiskAssessmentStatsComponent,
  DpiaEvaluationMapComponent
];

const EXPORTED_COMPONENTs = [
  ...DOCUMENT_STATS_AND_COMMENT,
  TextWithDialogComponent,
  CveDetailsTableComponent,
  InputErrorMessageComponent,
  InputUserChooserComponent,
  InputTextComponent,
  InputTextAreaComponent,
  InputNumberComponent,
  InputPasswordComponent,
  InputEmailComponent,
  InputUsernameComponent,
  InputPasswordHelpComponent,
  InputSelectComponent,
  InputPhoneComponent,
  RemoveBoxComponent,
  InputImageComponent,
  InputFilteredSelectComponent,
  InputFilteredSelectWithFooterActionComponent,
  InputFilteredMultipleSelectComponent,
  InputMultipleTextComponent,
  InputConfirmPasswordComponent,
  InputSelectVersionComponent,
  InputCheckboxComponent,
  InputRadioComponent,
  EditEntityPriorityDialogComponent,
  IncidentTeamDialogComponent,
  CreateDataBreachNoteDialogComponent,
  InputAttachmentComponent,
  InputUserPermissionSelectComponent,

  TreatmentInputComponent,
  TreatmentInputNavigationDataComponent,
  TreatmentInputRiskComponent,
  TreatmentInputStateComponent,
  TreatmentActionStateComponent,
  AiResultComponent,
  AppResultComponent,
  CovidResultComponent,

  TreatmentResultComponent,
  TreatmentDetailsResultComponent,

  EditorNotificationComponent,  // Todo da togliere alla fine di v2
  TemplateHeaderComponent,              // Todo da togliere alla fine di v2
  EditorLayoutComponent,        // Todo da togliere alla fine di v2
  TemplateEditorGroupsComponent,      // Todo da togliere alla fine di v2
  DocumentLockComponent,
  DocumentWorkInProgressComponent,
  TabChapterComponent,

  EditorSidebarComponent,
  EditorSummaryComponent,
  EditorSidebarPreviewBtnComponent,
  EditorSidebarMenuBtnComponent,
  EditorChapterStatusSelectorComponent,

  TreatmentEditorPlaceholderComponent,
  EditorSidebarPanelGroupComponent,

  TreatmentEditorComponent,
  GlobalMeasureCheckEditorComponent,
  AiAssEditorComponent,
  AppAssEditorComponent,
  CovidAssEditorComponent,
  DocumentEditorNotificationComponent,
  DocumentActionDialogComponent,
  WidgetNotificationComponent,
  PdfActionComponent,
  InputDocumentPermissionSelectorComponent,
  DbHistoryDialogComponent,
  ImpactMetricValueViewerComponent,
  RidViewerComponent
];

const WIDGET_CHECKBOX = [
  WidgetCheckboxComponent,
  WidgetCheckboxV0Component,
  WidgetCheckboxV0PreviewComponent,
  WidgetCheckboxV0EditComponent,
  WidgetCheckboxV0EditItemComponent,
  WidgetCheckboxV0EditChildrenComponent,

  WidgetCheckboxV0TreeComponent,

  WidgetCheckboxV0SelectComponent,
  WidgetCheckboxV0TreeDialogComponent
];

const WIDGET_CONTACT_CHOOSER = [
  WidgetContactChooserComponent,
  WidgetCheckboxV0TreeRendererComponent,
  WidgetContactChooserV0Component,
  WidgetContactChooserV0RowComponent,
  WidgetContactChooserV0PreviewComponent,
  WidgetContactChooserV0EditComponent
];

const WIDGET_RADIO_BUTTON = [
  WidgetRadiobuttonPreviewComponent,
  WidgetRadiobuttonComponent,
  WidgetRadiobuttonV0Component,
  WidgetRadiobuttonV0RadioComponent,
  WidgetRadiobuttonV0PreviewComponent,
  WidgetRadiobuttonV0SelectComponent
];

const WIDGET_DPIA = [
  WidgetDpiaComponent,
  WidgetDpiaV0Component,
  WidgetDpiaPreviewComponent,
  WidgetDpiaV0EditComponent,
  WidgetDpiaV0GravityValueSelectorComponent
];

const WIDGET_SIMPLE_RADIO_BUTTON = [
  WidgetSimpleRadioButtonComponent,
  WidgetSimpleRadiobuttonV0Component,
  WidgetSimpleRadiobuttonV0PreviewComponent
];

const WIDTET_TEXT = [
  WidgetTextV0PreviewComponent,
  WidgetTextV0AutosaveComponent,
  WidgetTextComponent,
  WidgetTextV0Component
];

const WIDTET_DATE = [
  WidgetDateComponent,
  WidgetDateV0Component,
  WidgetDateV0EditComponent,
  WidgetDateV0AutosaveComponent,
  WidgetDateV0PreviewComponent
];

const WIDTET_APP_ASSESSMENT = [
  WidgetAppAssessmentComponent,
  WidgetAppAssessmentV0Component,
  WaaEditorComponent,
  WidgetAppAssessmentTableHeaderV0Component,
  WaaEditRowCol1Component,
  WaaEditRowCol2Component,
  WaaEditRowCol3Component,
  WaaEditRowCol4Component
];

const WIDTET_APP_SUMMARY = [
  WidgetAppSummaryComponent,
  WidgetAppSummaryV0Component,
  WidgetAppSummaryPreviewComponent,
  WidgetAppSummaryPreviewV0Component,
  WidgetAppSummaryV0WarningComponent,
  WidgetAppSummaryRowV0Component,
  WidgetAppSummaryRowAppV0Component,
  WidgetAppSummaryRowRidV0Component,
  WidgetAppSummaryRowRidV2V0Component,
];

const WIDTET_RISK_ASSESSMENT = [
  WidgetRiskAssessmentComponent,
  WidgetLayoutComponent,
  WraV0Component,
  WraV0EditorComponent,
  WraV0ImplementedValueSelectorComponent,
  WraV0GlobalMeasureSelectorComponent
];

const WIDGET_PRELIMINARY_RISK_ASSESSMENT = [
  WidgetPreliminaryRiskAssessmentComponent,
  WidgetPreliminaryRiskAssessmentV0Component,
  WidgetPreliminaryRiskAssessmentV0TableComponent
];

const WIDGET_RECIPIENT_CHOOSER = [
  WidgetRecipientChooserComponent,
  WidgetRecipientChooserV0Component,
  WidgetRecipientChooserV0PreviewComponent,
  WidgetRecipientChooserV0EditComponent,

  WrcV0PreviewComponent,
  WrcV0PreviewComponent,
  WrcV0EditorComponent,
  WrcV0RowComponent
];

const WIDGET_DATA_RETENTION_AND_DELETION = [
  WidgetDataRetentionAndDeletionComponent,
  WidgetDataRetentionAndDeletionV0Component,
  WidgetDataRetentionAndDeletionV0RowComponent,
  WidgetDataRetentionAndDeletionHeaderV0Component,
  WidgetDataRetentionAndDeletionPreviewV0Component
];

const WIDGET_TREATMENT_DATA_INTERESTED = [
  WidgetTreatmentDataInterestedComponent,
  WidgetTreatmentDataInterestedV0Component,
  WidgetTreatmentDataInterestedV0EditComponent,
  WidgetTreatmentDataInterestedV0PreviewComponent,
  WidgetTreatmentDataInterestedV0TableComponent,
  WidgetTreatmentDataInterestedV0ListCheckboxComponent,
  WidgetTreatmentDataInterestedV0GroupCheckboxComponent
];

const WIDGET_MULTI_QUESTION = [
  WidgetMultiQuestionComponent,
  WidgetMultiQuestionV0Component,
  WidgetMultiQuestionV0PreviewComponent,
  WidgetMultiQuestionV0EditorComponent,
  WidgetMultiQuestionV0RadioButtonComponent,
  WidgetMultiQuestionV0CheckButtonComponent,
  WidgetMultiQuestionV0TextComponent
];

const WIDGET_ATTACHMENT = [
  WidgetAttachmentComponent,
  WidgetAttachmentV0Component,
  WidgetAttachmentEditorV0Component,
  WidgetAttachmentPreviewV0Component
];

const WIDGET_HTML = [
  WidgetHtmlPreviewComponent,
  WidgetHtmlComponent,
  WidgetHtmlV0Component,
  WidgetHtmlV0PreviewComponent
];

const TREATMENT_EDITOR = [
  TemplateHeaderComponent,
  TemplateHeaderActionsComponent,
  TemplateCompleteStatusComponent,
  TabWithScrollComponent,
  EditorNotificationComponent,
  TemplateEditorWidgetGroupComponent,
  TemplateEditorViewSelectorComponent,
  TemplateEditorGroupComponent
];

const WINTERNAL_WIDGETs = [

  ...TREATMENT_EDITOR,
  ...WIDGET_DPIA,
  ...WIDGET_CHECKBOX,
  ...WIDGET_CONTACT_CHOOSER,
  ...WIDGET_RADIO_BUTTON,
  ...WIDGET_SIMPLE_RADIO_BUTTON,
  ...WIDTET_TEXT,
  ...WIDGET_PRELIMINARY_RISK_ASSESSMENT,
  ...WIDGET_RECIPIENT_CHOOSER,
  ...WIDGET_DATA_RETENTION_AND_DELETION,


  ...WIDGET_TREATMENT_DATA_INTERESTED,
  ...WIDTET_APP_ASSESSMENT,
  ...WIDTET_APP_SUMMARY,
  ...WIDTET_RISK_ASSESSMENT,
  ...WIDGET_MULTI_QUESTION,
  ...WIDGET_ATTACHMENT,
  ...WIDGET_HTML,
  ...WIDTET_DATE,

  WidgetInputNoteComponent,
  WidgetDisabledComponent,
  WidgetVersionMissingComponent,
  WidgetHeaderComponent,
  WidgetNodataComponent,
  WidgetActionComponent,
  WidgetFooterComponent,
  WidgetPreliminaryRiskAssessmentComponent,
  WidgetNotSupportedComponent,
  FullDialogHeaderComponent,
  DocumentHistoryDialogComponent,
  WidgetRecipientChooserV0RowComponent,
  WidgetErrorComponent,
  EditorSidebarTextComponent,
  EditorSidebarNavigationDataComponent,
  EditorSidebarLinkComponent,


  WidgetSelectComponent

];

const WDIALOGS = [
  HtmlPreviewDialogComponent,
  TextareaDialogComponent,
  CommentDialogComponent,
  CompleteStatusDialogComponent,
  WidgetConfirmDialogComponent,
  ConfirmDeleteDocumentValidatedDialogComponent,
  CreateNewDocumentResponseDialogComponent,
  UserCheckedDialogComponent,
  ValidateRequestDialogComponent,
  ConfirmDeleteDataProtectionGovernanceItemDialogComponent,
  UpdateDataBreachDialogComponent,
  CreateSnapshotDialogComponent,
  ConfirmObsoleteDialogComponent,
];

const WDIRECTIVEs = [
  WidgetSelectorDirective
];

const RENDERERs = [
  ScenarioFormatterRendererComponent,
  ApplicationNameCellRendererComponent,
  BooleanCellRendererComponent,
  StatusFormatterRendererComponent,
  TranslateServiceRendererComponent,
  CustomRendererComponent,
  LockFormatterRendererComponent,
  ListFormatterRendererComponent,
  DateFormatterRendererComponent,
  BooleanToAdverbsRendererComponent,
  EnumRendererComponent,
  EnumToAdverbsFilterCellRendererComponent,
  RegulationTypeRendererComponent,
  LevelFormatterRendererComponent,
  ImageRendererComponent,
  ListIndexFormatterRendererComponent,
  OverlayLoadingRendererComponent,
  ResourceSubTypeFormatterRendererComponent,
  FooterRenderComponent,
  FileSizeFormatterRendererComponent,
  ProgressFormatterRendererComponent,
  SeverityFormatterRendererComponent,
  SelectCellEditableRendererComponent,
  TextCellEditableRendererComponent,
  CheckboxRendererComponent,
  CveDetailsRendererComponent
];

const DETAILS_COMPONENTs = [
  DetailsSecurityToolTypeInstructionsComponent,
  DetailsPageWrapperComponent,
  DetailsPageHeaderComponent,
  DetailsTableComponent,
  DetailsAttributeComponent,
  DetailsChartComponent,
  RequiredReloadComponent,
  DetailsSingleValueComponent,
  DetailsCardComponent,
  DetailsListCardComponent,
  EditorListCardComponent,
  DetailsTitleComponent,
  DetailsViewerComponent,
  DetailsAttachmentsComponent,
  DetailsAttributeViewerComponent,
  DetailsImageComponent,
  DetailsReminderComponent,
  DetailsAdministratorsComponent,
  DetailsSecurityToolTypeComponent,
  DetailsLayoutComponent,
  DetailsApplicationAuditComponent,
  DetailsApplicationCveComponent,
  DetailsApplicationAuditCveSeverityComponent
];

const COMPONENTs = [
  ...DETAILS_COMPONENTs,

  EditHelpComponent,
  InputLabelComponent,
  InputUserGroupSelectComponent,
  InputTextConditionalCheckboxComponent,
  InputTimeDatePickerComponent,
  InputDatePickerComponent,
  EntityIconComponent,
  HeaderComponent,
  LoaderComponent,
  LoadingPageComponent,
  IconComponent,
  MissingImgDirective,
  WarningComponent,
  InfoComponent,
  ActionBarComponent,
  ButtonWithLoaderComponent,
  FormActionsComponent,
  UpdateFormFooterActionsComponent,
  LabelItemComponent,
  FormSectionContainerComponent,
  FormContainerComponent,
  BooleanToAdverbFilterComponent,
  EnumToAdverbFilterComponent,
  StorageComponent,
  DashboardLayoutComponent,
  NavbarComponent,
  SidebarComponent,
  SidebarItemComponent,
  SidebarItemLinkComponent,
  SidebarItemLogoComponent,
  SidebarItemMenuComponent,
  SidebarItemTitleComponent,
  SidebarItemToogleComponent,
  SidebarItemStorageComponent,
  SidebarItemFooterComponent,
  SidebarItemButtonComponent,
  BreadcrumbComponent,
  DashboardCardComponent,
  ExtractionCardComponent,
  NavItemComponent,
  NavbarUserItemComponent,
  NavbarUserItemLayoutComponent,
  NavbarAnyPermissionComponent,
  NavbarSessionNotificationItemComponent,
  NavbarLogoItemComponent,
  CandidatesMenuItemComponent,
  NavbarButtonItemComponent,
  NavbarSupportItemComponent,
  NavbarMenuItemComponent,
  NavbarLegalEntityItemComponent,
  TableColActionsComponent,
  TablePageLayoutComponent,
  ResourceNavigationComponent,
  SessionNotificationComponent,
  SessionNotificationItemComponent,
  LevelFormatterRendererComponent,
  LevelFormatterComponent,
  AttachmentsWorkspaceTableComponent,
  AttachmentsWorkspaceBreadcrumbComponent,
  InputCodeFormComponent,
  SelectRecipientTableComponent,
  SelectUserResourceTableComponent,
  LoaderComponent,
  NoActiveModuleComponent,
  NoActiveManagementModuleComponent,
  DeleteSelectedRowsComponent,
  DownloadSelectedRowsComponent,
  CustomTooltipComponent,
  CustomDateFilterComponent,
  DeleteWithConfirmComponent,
  ScrollableNavTabComponent,
  UnexpectedErrorComponent,
  TableFilterComponent,
];

const DIALOGs = [
  VendorAndProductDialogComponent,
  EditDocumentValidatedDialogComponent,
  ImportFileWithCheckComponent,
  CreateFolderDialogComponent,
  ShowAllAttributesDialogComponent,
  SystemLogDialogComponent,
  MoveItemDialogComponent,
  HtmlHelpDialogComponent,
  SelectRecipientDialogComponent,
  SelectUserResourceDialogComponent,
  AttachFileDialogComponent,
  NewGroupDialogComponent,
  RenameResourceDialogComponent,
  CreateSupplierDialogComponent,
  DocumentSettingsDialogComponent,
  ErrorDependencyDialogComponent,
  PermissionDialogComponent,
  PermissionIconButtonComponent,
  TreatmentInfoDialogComponent
];

const PIPES = [
  LocalizedDatePipe,
  LatestChangeDatePipe,
  ReplaceNullWithTextPipe,
  FormatNewlineToHtmlPipe,
  RemoveHelpPrefixPipe,
  SafeUrlPipe,
  HighlightPipe,
  FormatTextPipe,
  AttachmentTypePipe,
  ReferenceTitlePipe
];


const AUTH_COMPONENTs = [
  SignInComponent,
  OtpInputComponent,
  ImpersonationDialogComponent,
  SignLayoutComponent,
  SignHeaderComponent,
  ForgotPasswordComponent,
  ForgotPasswordSendMailComponent,
  ForgotPasswordUpdateComponent,
  ForgotMessageComponent,
  SignActionsComponent,
  AuthImageAreaComponent

];

const TEST_COMPONENT = [];


@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    NgxPermissionsModule,
    TranslateModule.forChild(),
    NgbModule,
    NgbPopoverModule,
    FormsModule,
    ReactiveFormsModule,
    SidebarModule.forRoot(),
    ApiClientModule,
    AgGridModule,
    NgbAlertModule,
    IonicModule.forRoot(),
    QuillModule.forRoot(),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    CodemirrorModule,
    NgbAccordionModule,
    NgbProgressbarModule,
    NgbNavModule,
    NgbTooltipModule,
    PdfViewerModule,
    NgbTimepickerModule,
    NgbDatepickerModule,
    NgbCollapseModule,
    NgSelectModule,
    SidebarModule,
    TreeModule,
    DetailsSecurityToolTypeSupplierComponent,
    DetailsSecurityToolTypeVendorOrProductComponent,
    IfDeleteActionDirective,
    CtrlClickDirective,
    DevWidgetInfoComponent,
    WidgetHelpComponent,
    InputTimestampComponent,
    WMeasureCategoryTypeBadgeComponent,
    OperationTypeBadgeViewerComponent
  ],
  declarations: [
    ...TEST_COMPONENT,
    ...COMPONENTs,
    ...AUTH_COMPONENTs,
    ...DIALOGs,
    ...RENDERERs,
    ...PIPES,
    ...WDIRECTIVEs,
    ...EXPORTED_COMPONENTs,
    ...WDIALOGS,
    ...WINTERNAL_WIDGETs,
    EditorSidebarDownloadWordBtnComponent,
  ],
  exports: [
    ...COMPONENTs,
    ...AUTH_COMPONENTs,
    ...DIALOGs,
    ...RENDERERs,
    ...PIPES,
    ...EXPORTED_COMPONENTs,
    ...WDIALOGS,
    EditorSidebarTextComponent,
    WidgetActionComponent,
    TemplateCompleteStatusComponent,
    TemplateEditorWidgetGroupComponent,
    WidgetNodataComponent,
  ],
})
export class PtSharedModule {
  static forRoot(
    config?: PtWidgetModuleConfig
  ): ModuleWithProviders<PtSharedModule> {
    return {
      ngModule: PtSharedModule,
      providers: [
        {
          provide: WidgetConfig,
          useValue: config,
        },
      ],
    };
  }
}
